import { useEffect, useRef } from "react";
import { trackEvent, trackDebug } from "@/lib/trackEvent";

const MILESTONES = [25, 50, 75, 100];

/** Fires `article_scroll_depth` once per milestone (25/50/75/100%) per article. */
export function useScrollDepth(enabled: boolean, meta: Record<string, unknown>) {
  const fired = useRef<Set<number>>(new Set());
  const metaRef = useRef(meta);
  metaRef.current = meta;

  useEffect(() => {
    if (!enabled) return;
    fired.current = new Set();

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
      for (const m of MILESTONES) {
        if (pct >= m && !fired.current.has(m)) {
          fired.current.add(m);
          // Only the "actually read it" milestone is a product event; the
          // intermediate milestones stay on the debug channel.
          if (m === 75) {
            trackEvent("article_read", { ...metaRef.current, depth: m });
          } else {
            trackDebug("article_scroll_depth", { ...metaRef.current, depth: m });
          }
        }
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [enabled]);
}
